import { useState } from "react";
import { Challenge } from "@cleverbase-trust-drivers/shared/Domain";
import styled, { keyframes } from "styled-components";
import QRCode from "qrcode.react";
import numberBox from "../../../images/number-box.svg";
import { ConsentItems } from "../../consent/requesting/ConsentItems";
import { UseCase } from "../../base/UseCase";

const Containter = styled.div<{ $manual: boolean; $isDesktop: boolean }>`
  min-width: ${({ $isDesktop }) => ($isDesktop ? "160px" : "260px")};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const QrBlockCss = {
  display: "block",
};

const ManualEntryFooter = styled.div`
  position: absolute;
  left: 50%;
  top: calc(100% + 24px);
  transform: translateX(-50%);
`;

const ButtonManualScan = styled.button`
  cursor: pointer;
  background: transparent;
  color: #23231f;
  border: 1px solid #23231f;
  border-radius: 4px;
  padding: 16px 12px 16px 16px;
  height: 56px;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  width: fit-content;
  white-space: nowrap;

  &:hover {
    background: #163563;
    color: #f7f7f6;
    border-color: transparent;
  }

  &:focus-visible {
    outline: none;
    background: #163563;
    color: #f7f7f6;
    border-color: transparent;
    box-shadow:
      0 0 0 2px #f7f7f6,
      0 0 0 4px #126de3;
  }
`;

const ManualEntryIcon = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ManualEntryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const QrFrame = styled.div`
  width: 200px;
  height: 200px;
  background: #163563;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const QrCaption = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QrCaptionIcon = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const QrCaptionText = styled.span`
  && {
    color: #55544c;
    font-size: 14px;
    line-height: 20px;
    font-family: "Inter", sans-serif;
    font-weight: 400;
  }
`;

const QrCaptionInline = styled.span`
  && {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    color: #55544c;
    font-size: 14px;
    line-height: 20px;
    font-family: "Inter", sans-serif;
    font-weight: 400;
  }
`;

const placeHolderShimmer = keyframes`
  0% {
      background-position: -468px 0
  }
  100% {
      background-position: 468px 0
  }
  `;

export const EmptyBackgroundAnimation = styled.span`
  animation-duration: 1.25s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: ${placeHolderShimmer};
  animation-timing-function: linear;
  background: #f6f6f6;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.1) 8%,
    rgba(0, 0, 0, 0.2) 18%,
    rgba(0, 0, 0, 0.1) 33%
  );
  background-size: 1024px 104px;
  position: relative;
  border-radius: 4px;
`;

const EmptyNonce = styled(EmptyBackgroundAnimation)`
  width: 60%;
  height: 30px;
  margin-top: 10px;
  margin-bottom: 20px;
`;

const ManualEntryBox = styled.div`
  background: #163563;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DigitRow = styled.div`
  display: flex;
  gap: 8px;
`;

const DigitGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const DigitBox = styled.div`
  width: 24px;
  height: 40px;
  background: url(${numberBox}) no-repeat center;
  background-size: 24px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DigitText = styled.span`
  font-family: ${({ theme }) => theme.headingFontFamily};
  font-weight: 400;
  font-size: 20px;
  line-height: 20px;
  color: #f7f7f6;
`;

const Button = styled.button`
  display: flex;
  border: 1px solid #23231f;
  padding: 16px 12px 16px 16px;
  height: 56px;
  background: transparent;
  border-radius: 4px;
  color: #23231f;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  width: fit-content;

  &:hover {
    background: #163563;
    color: #f7f7f6;
    border-color: transparent;
  }

  &:focus-visible {
    outline: none;
    background: #163563;
    color: #f7f7f6;
    border-color: transparent;
    box-shadow:
      0 0 0 2px #f7f7f6,
      0 0 0 4px #126de3;
  }
`;

const GreyButton = styled(Button)`
  border: 1px solid transparent;
  background: none;
`;

const ArrowIcon = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const AppLocationText = styled.p`
  font-size: 1.6rem;
  margin: 0 0 24px 0;
  color: #23231f;
`;

const DeviceButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const HelpButton = styled.button`
  border: none;
  background-color: transparent;
  color: #23231f;
  margin-top: 20px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;

  &:hover {
    text-decoration: underline;
  }
`;

const ChevronLeftIcon = styled.span`
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

function StyledNonceGroup(group: string, key: number): JSX.Element {
  const items = group.split("");
  return (
    <DigitGroup key={key}>
      {items.map((digit, index) => (
        <DigitBox key={`${key}-${index}`} data-testid={`noncedigit-${index}`}>
          <DigitText>{digit}</DigitText>
        </DigitBox>
      ))}
    </DigitGroup>
  );
}

function StyledQRNonce(nonce: string | undefined) {
  if (!nonce)
    return <EmptyNonce data-testid={`empty-content-nonce-"cleverbase"`} />;

  const nonceGroups = nonce
    .split("")
    .reduce((acc, val, i) => acc + (i % 3 === 0 ? " " + val : val), "")
    .trim()
    .split(" ");
  return (
    <DigitRow data-testid="nonce-container-ck">
      {nonceGroups.map((group, i) => StyledNonceGroup(group, i))}
    </DigitRow>
  );
}

export interface AuthenticatingProps {
  clientName?: string | undefined;
  clientContextText?: string | undefined;
  ckChallenge: Challenge | undefined;
  isDesktop: boolean;
  identityAttributes: Array<string>;
  showClientContext: (visibile: boolean) => void;
  displayClientConsentContext: (visibile: boolean) => void;
  t: AuthenticatingText;
  noAuthenticationSpinner: boolean;
  useCase: UseCase;
}

export interface AuthenticatingText {
  manualEntry: string;
  qrCodeEntry: string;
  confirmWithApp: string;
  AppLocation: string;
  AppLocationTextOnThisDevice: string;
  AppLocationTextDifferentDevice: string;
  confirmationText: string;
  disclaimerText: string;
  clientContextHeaderText: string;
  onlyOpenIdScopeText: string;
  returnToChooseApplication: string;
  openCleverbaseAppToContinue: string;
}

export default function Authenticating({
  clientName,
  ckChallenge,
  isDesktop,
  identityAttributes,
  showClientContext,
  displayClientConsentContext,
  t,
  noAuthenticationSpinner,
  clientContextText,
  useCase,
}: AuthenticatingProps): JSX.Element {
  const noAuthenticationSpinnerText = noAuthenticationSpinner
    ? "&noAuthenticationSpinner=true"
    : "";

  const useCaseText = `&usecase=${useCase.toLowerCase()}`;

  const [manualEntry, toggleManualEntry] = useState<boolean>(false);
  const [onThisDevice, toggleOnThisDevice] = useState<boolean>(true);
  const [continueOnThisDevice, setContinueOnThisDevice] =
    useState<boolean>(false);

  function createQrCodeValue(ckChallenge: Challenge | undefined): string {
    return `vidua://?nonce=${ckChallenge?.nonce.value}&ck=${ckChallenge?.nonce.value}&ckloader=true${noAuthenticationSpinnerText}${useCaseText}`;
  }

  const url: string = `vidua://?nonce=${ckChallenge?.nonce
    .value}${noAuthenticationSpinnerText}${useCaseText}&triggeredOnDevice=true&callbackURL=${encodeURIComponent(
    window.location.toString(),
  )}`;

  const captionMatch =
    t.openCleverbaseAppToContinue.match(/^(.*\bapp\b)(.*)$/i);
  const captionBefore = captionMatch?.[1];
  const captionAfter = captionMatch?.[2];

  return (
    <Containter
      $manual={manualEntry}
      $isDesktop={isDesktop}
      data-testid="authentication_authenticating"
    >
      {!isDesktop && onThisDevice ? (
        continueOnThisDevice ? (
          <>
            <ConsentItems
              confirmationText={t.confirmationText}
              disclaimerText={t.disclaimerText}
              toShareAttributes={identityAttributes}
              isDesktop={isDesktop}
              onlyOpenIdScopeText={t.onlyOpenIdScopeText}
            />
            <Button
              data-testid="authenticating_confirm_with_app"
              onClick={() => window.open(url, "_self")}
            >
              {t.confirmWithApp}
            </Button>
            <HelpButton
              onClick={() => {
                setContinueOnThisDevice(false);
                displayClientConsentContext(false);
              }}
            >
              <ChevronLeftIcon aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 -960 960 960"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M456-480 640-296l-56 56-240-240 240-240 56 56-184 184Z" />
                </svg>
              </ChevronLeftIcon>
              {t.returnToChooseApplication}
            </HelpButton>
          </>
        ) : (
          <>
            <AppLocationText>{t.AppLocation}</AppLocationText>
            <DeviceButtonGroup>
              <Button
                onClick={() => {
                  setContinueOnThisDevice(true);
                  displayClientConsentContext(clientContextText !== undefined);
                }}
                data-testid="authenticating_continue_on_this_device"
              >
                {t.AppLocationTextOnThisDevice}
                <ArrowIcon aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24px"
                    viewBox="0 -960 960 960"
                    width="24px"
                    fill="currentColor"
                  >
                    <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
                  </svg>
                </ArrowIcon>
              </Button>
              <GreyButton
                onClick={() => toggleOnThisDevice(false)}
                data-testid="authenticating_continue_on_other_device"
              >
                {t.AppLocationTextDifferentDevice}
                <ArrowIcon aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24px"
                    viewBox="0 -960 960 960"
                    width="24px"
                    fill="currentColor"
                  >
                    <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
                  </svg>
                </ArrowIcon>
              </GreyButton>
            </DeviceButtonGroup>
          </>
        )
      ) : (
        <>
          {!manualEntry ? (
            <>
              <QrFrame>
                <QRCode
                  style={QrBlockCss}
                  level="Q"
                  fgColor="#FFFFFF"
                  bgColor="#163563"
                  size={160}
                  value={createQrCodeValue(ckChallenge)}
                  data-testid="authenticating_qr"
                />
              </QrFrame>
              <QrCaption>
                <QrCaptionText>
                  {captionBefore ? (
                    <>
                      <QrCaptionInline>
                        {captionBefore}
                        <QrCaptionIcon aria-hidden="true">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="24"
                              height="24"
                              rx="8"
                              fill="#371F13"
                            />
                            <path
                              d="M16.9007 14.7281C16.911 14.8303 16.9161 14.9606 16.9161 15.119C16.9161 15.2722 16.9059 15.4511 16.8854 15.6554C16.8701 15.8547 16.8497 16.0488 16.8241 16.2378C16.7577 16.667 16.6913 16.9735 16.6249 17.1575C15.5315 17.7041 14.1112 17.9774 12.364 17.9774C10.7291 17.9774 9.33437 17.5457 8.17974 16.6823C7.33676 16.0539 6.74667 15.1726 6.40948 14.0384C6.23578 13.456 6.14893 12.7484 6.14893 11.9157C6.14893 11.0778 6.30986 10.2884 6.63172 9.54765C6.95359 8.80174 7.40318 8.16823 7.98049 7.64711C9.1811 6.56401 10.7393 6.02246 12.6552 6.02246C13.0128 6.02246 13.4803 6.05567 14.0576 6.12209C14.6349 6.1885 15.013 6.22937 15.1918 6.2447L15.7129 6.29835C16.1063 6.33411 16.3643 6.35199 16.4869 6.35199H16.7322C16.7679 6.35199 16.8241 6.34688 16.9007 6.33666C16.911 6.45928 16.9161 6.74793 16.9161 7.20263C16.9161 7.65733 16.8343 8.42112 16.6708 9.494H15.6899C15.5929 8.72255 15.4702 8.18866 15.3221 7.89234C14.898 7.21285 13.9708 6.8731 12.5402 6.8731C11.1097 6.8731 9.99342 7.34313 9.19132 8.28318C8.42497 9.17725 8.0418 10.36 8.0418 11.8314C8.0418 13.5326 8.52204 14.8635 9.48253 15.824C10.3613 16.7028 11.4342 17.1421 12.7012 17.1421C13.6157 17.1421 14.3105 16.9684 14.7856 16.621C15.2659 16.2685 15.6516 15.6375 15.9428 14.7281H16.9007Z"
                              fill="#FAF5EB"
                            />
                          </svg>
                        </QrCaptionIcon>
                      </QrCaptionInline>
                      {captionAfter}
                    </>
                  ) : (
                    t.openCleverbaseAppToContinue
                  )}
                </QrCaptionText>
              </QrCaption>
            </>
          ) : (
            <ManualEntryContainer data-testid="authenticating_manual_entry">
              <ManualEntryBox>
                {StyledQRNonce(ckChallenge?.nonce.value)}
              </ManualEntryBox>
              <QrCaption>
                <QrCaptionText>
                  {captionBefore ? (
                    <>
                      <QrCaptionInline>
                        {captionBefore}
                        <QrCaptionIcon aria-hidden="true">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="24"
                              height="24"
                              rx="8"
                              fill="#371F13"
                            />
                            <path
                              d="M16.9007 14.7281C16.911 14.8303 16.9161 14.9606 16.9161 15.119C16.9161 15.2722 16.9059 15.4511 16.8854 15.6554C16.8701 15.8547 16.8497 16.0488 16.8241 16.2378C16.7577 16.667 16.6913 16.9735 16.6249 17.1575C15.5315 17.7041 14.1112 17.9774 12.364 17.9774C10.7291 17.9774 9.33437 17.5457 8.17974 16.6823C7.33676 16.0539 6.74667 15.1726 6.40948 14.0384C6.23578 13.456 6.14893 12.7484 6.14893 11.9157C6.14893 11.0778 6.30986 10.2884 6.63172 9.54765C6.95359 8.80174 7.40318 8.16823 7.98049 7.64711C9.1811 6.56401 10.7393 6.02246 12.6552 6.02246C13.0128 6.02246 13.4803 6.05567 14.0576 6.12209C14.6349 6.1885 15.013 6.22937 15.1918 6.2447L15.7129 6.29835C16.1063 6.33411 16.3643 6.35199 16.4869 6.35199H16.7322C16.7679 6.35199 16.8241 6.34688 16.9007 6.33666C16.911 6.45928 16.9161 6.74793 16.9161 7.20263C16.9161 7.65733 16.8343 8.42112 16.6708 9.494H15.6899C15.5929 8.72255 15.4702 8.18866 15.3221 7.89234C14.898 7.21285 13.9708 6.8731 12.5402 6.8731C11.1097 6.8731 9.99342 7.34313 9.19132 8.28318C8.42497 9.17725 8.0418 10.36 8.0418 11.8314C8.0418 13.5326 8.52204 14.8635 9.48253 15.824C10.3613 16.7028 11.4342 17.1421 12.7012 17.1421C13.6157 17.1421 14.3105 16.9684 14.7856 16.621C15.2659 16.2685 15.6516 15.6375 15.9428 14.7281H16.9007Z"
                              fill="#FAF5EB"
                            />
                          </svg>
                        </QrCaptionIcon>
                      </QrCaptionInline>
                      {captionAfter}
                    </>
                  ) : (
                    t.openCleverbaseAppToContinue
                  )}
                </QrCaptionText>
              </QrCaption>
            </ManualEntryContainer>
          )}

          <ManualEntryFooter>
            <ButtonManualScan
              onClick={() => {
                toggleManualEntry(!manualEntry);
                showClientContext(!!clientName || manualEntry);
              }}
              data-testid="authenticating_entry_toggle"
            >
              {manualEntry ? t.qrCodeEntry : t.manualEntry}
              <ManualEntryIcon aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 -960 960 960"
                  width="24px"
                  fill="currentColor"
                >
                  <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
                </svg>
              </ManualEntryIcon>
            </ButtonManualScan>
          </ManualEntryFooter>
          {!isDesktop && (
            <HelpButton onClick={() => toggleOnThisDevice(true)}>
              <ChevronLeftIcon aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 -960 960 960"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M456-480 640-296l-56 56-240-240 240-240 56 56-184 184Z" />
                </svg>
              </ChevronLeftIcon>
              {t.returnToChooseApplication}
            </HelpButton>
          )}
        </>
      )}
    </Containter>
  );
}
